<template>
  <div class="map-picker">
    <el-input 
      :model-value="displayText" 
      :placeholder="placeholder"
      @click="openDialog"
      readonly
    >
      <template #append>
        <el-button @click="openDialog">
          <el-icon><Location /></el-icon>
          选点
        </el-button>
      </template>
    </el-input>
    
    <el-dialog 
      v-model="showMapDialog" 
      title="选择位置" 
      width="900px"
      :close-on-click-modal="false"
      :destroy-on-close="true"
      @close="handleDialogClose"
    >
      <div class="map-container">
        <div class="search-box">
          <el-input 
            v-model="searchKeyword" 
            placeholder="搜索地址"
            @keyup.enter="searchLocation"
          >
            <template #prepend>
              <el-select v-model="currentProvider" style="width: 100px" @change="switchProvider">
                <el-option label="腾讯地图" value="tencent" />
                <el-option label="百度地图" value="baidu" />
                <el-option label="高德地图" value="amap" />
              </el-select>
            </template>
            <template #append>
              <el-button @click="searchLocation" :loading="searching">搜索</el-button>
            </template>
          </el-input>
        </div>
        
        <!-- 搜索结果列表 -->
        <div class="search-results" v-if="searchResults.length > 0">
          <el-scrollbar max-height="150px">
            <div 
              v-for="(item, index) in searchResults" 
              :key="index" 
              class="search-result-item"
              @click="selectSearchResult(item)"
            >
              <div class="result-title">{{ item.title }}</div>
              <div class="result-address">{{ item.address }}</div>
            </div>
          </el-scrollbar>
        </div>
        
        <div class="map-status" v-if="!mapConfig.enabled">
          <el-alert type="warning" :closable="false">
            地图服务未启用，请在系统设置中配置地图服务
          </el-alert>
        </div>
        
        <div id="mapContainer" class="map-view" v-show="mapConfig.enabled"></div>
        
        <div class="location-info" v-if="selectedLocation">
          <p><strong>地址：</strong>{{ selectedLocation.address }}</p>
          <p><strong>经度：</strong>{{ selectedLocation.lng }}</p>
          <p><strong>纬度：</strong>{{ selectedLocation.lat }}</p>
        </div>
      </div>
      <template #footer>
        <el-button @click="handleCancel">取消</el-button>
        <el-button type="primary" @click="confirmLocation" :disabled="!selectedLocation">确认选择</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, onMounted, watch, nextTick, computed } from 'vue'
import { ElMessage } from 'element-plus'
import { Location } from '@element-plus/icons-vue'
import axios from 'axios'

const props = defineProps({
  modelValue: {
    type: String,
    default: ''
  },
  latitude: {
    type: [Number, String],
    default: null
  },
  longitude: {
    type: [Number, String],
    default: null
  },
  placeholder: {
    type: String,
    default: '点击选择位置'
  }
})

// 计算显示的文本：优先显示地址，没有地址则显示经纬度
const displayText = computed(() => {
  if (props.modelValue) {
    return props.modelValue
  }
  if (props.latitude && props.longitude) {
    return `经度: ${props.longitude}, 纬度: ${props.latitude}`
  }
  return ''
})

const emit = defineEmits(['update:modelValue', 'update:latitude', 'update:longitude'])

const showMapDialog = ref(false)
const searchKeyword = ref('')
const addressText = ref(props.modelValue || '')
const selectedLocation = ref(null)
const currentProvider = ref('tencent')
const searching = ref(false)
const searchResults = ref([])
let searchService = null

const mapConfig = ref({
  enabled: true,
  provider: 'tencent',
  tencentKey: '',
  baiduAk: '',
  amapKey: '',
  amapSecret: '',
  defaultCity: '北京',
  defaultZoom: 15
})

let map = null
let marker = null
const mapLoaded = ref(false)

watch(() => props.modelValue, (newVal) => {
  addressText.value = newVal || ''
})

// 同时监听经纬度变化
watch([() => props.latitude, () => props.longitude], () => {
  // 经纬度变化时不需要特殊处理，displayText会自动更新
})

const fetchMapConfig = async () => {
  try {
    const response = await axios.get('https://tory.ffdzheng.com/api/settings/map/config')
    if (response.data?.code === 200 && response.data?.data) {
      const data = response.data.data
      mapConfig.value = {
        enabled: data.enabled !== false,
        provider: data.provider || 'tencent',
        tencentKey: data.tencentKey || '',
        baiduAk: data.baiduAk || '',
        amapKey: data.amapKey || '',
        amapSecret: data.amapSecret || '',
        defaultCity: data.defaultCity || '北京',
        defaultZoom: data.defaultZoom || 15
      }
      currentProvider.value = mapConfig.value.provider
    }
  } catch (error) {
    console.warn('获取地图配置失败:', error)
  }
}

const openDialog = () => {
  showMapDialog.value = true
}

const handleCancel = () => {
  searchResults.value = []
  selectedLocation.value = null
  showMapDialog.value = false
}

const handleDialogClose = () => {
  // 清理地图实例
  if (map) {
    map.destroy && map.destroy()
    map = null
  }
  marker = null
  searchService = null
  searchResults.value = []
  selectedLocation.value = null
}

watch(showMapDialog, async (val) => {
  if (val) {
    searchResults.value = []
    selectedLocation.value = null
    await nextTick()
    setTimeout(() => {
      if (showMapDialog.value) {
        initMap()
      }
    }, 100)
  }
})

const switchProvider = () => {
  map = null
  marker = null
  searchService = null
  mapLoaded.value = false
  searchResults.value = []
  setTimeout(() => initMap(), 100)
}

const initMap = () => {
  if (!mapConfig.value.enabled) return
  
  const container = document.getElementById('mapContainer')
  if (!container) return
  
  const provider = currentProvider.value
  
  if (provider === 'tencent') {
    initTencentMap(container)
  } else if (provider === 'baidu') {
    initBaiduMap(container)
  } else if (provider === 'amap') {
    initAmap(container)
  }
}

const initTencentMap = (container) => {
  if (!mapConfig.value.tencentKey) {
    ElMessage.warning('请先配置腾讯地图Key')
    return
  }
  
  if (!window.TMap) {
    loadScript('https://map.qq.com/api/gljs?v=1.exp&key=' + mapConfig.value.tencentKey + '&libraries=service', () => {
      createTencentMap(container)
    })
  } else {
    createTencentMap(container)
  }
}

const createTencentMap = (container) => {
  const center = new window.TMap.LatLng(39.984104, 116.307503)
  
  map = new window.TMap.Map(container, {
    center: center,
    zoom: mapConfig.value.defaultZoom,
    viewMode: '2D'
  })
  
  marker = new window.TMap.MultiMarker({
    map: map,
    geometries: []
  })
  
  searchService = new window.TMap.service.Search({ pageSize: 10 })
  
  map.on('click', (evt) => {
    const { lat, lng } = evt.latLng
    updateMarker(lat, lng)
    reverseGeocodeTencent(lat, lng)
  })
  
  mapLoaded.value = true
}

const reverseGeocodeTencent = (lat, lng) => {
  const geocoder = new window.TMap.service.Geocoder()
  
  geocoder.getAddress({ location: new window.TMap.LatLng(lat, lng) })
    .then((result) => {
      let address = ''
      if (result.result) {
        const addr = result.result
        if (addr.formatted_addresses && addr.formatted_addresses.recommend_address) {
          address = addr.formatted_addresses.recommend_address
        } else if (addr.formatted_addresses && addr.formatted_addresses.rough_address) {
          address = addr.formatted_addresses.rough_address
        } else if (addr.address) {
          address = (addr.address.province || '') + (addr.address.city || '') + (addr.address.district || '') + (addr.address.street || '') + (addr.address.street_number || '')
        }
      }
      
      if (!address || address.length < 2) {
        address = `位置: ${lng.toFixed(6)}, ${lat.toFixed(6)}`
      }
      
      selectedLocation.value = { lat, lng, address }
    })
    .catch((error) => {
      console.warn('逆地理编码失败:', error)
      selectedLocation.value = {
        lat, lng,
        address: `位置: ${lng.toFixed(6)}, ${lat.toFixed(6)}`
      }
    })
}

const initBaiduMap = (container) => {
  if (!mapConfig.value.baiduAk) {
    ElMessage.warning('请先配置百度地图AK')
    return
  }
  
  if (!window.BMap) {
    loadScript('https://api.map.baidu.com/api?v=3.0&ak=' + mapConfig.value.baiduAk + '&callback=baiduMapInit', () => {})
    window.baiduMapInit = () => createBaiduMap(container)
  } else {
    createBaiduMap(container)
  }
}

const createBaiduMap = (container) => {
  const point = new window.BMap.Point(116.404, 39.915)
  
  map = new window.BMap.Map(container)
  map.centerAndZoom(point, mapConfig.value.defaultZoom)
  map.enableScrollWheelZoom()
  
  marker = new window.BMap.Marker(point)
  
  map.addEventListener('click', (evt) => {
    const { lng, lat } = evt.point
    updateMarker(lat, lng)
    reverseGeocodeBaidu(lat, lng)
  })
  
  mapLoaded.value = true
}

const reverseGeocodeBaidu = (lat, lng) => {
  const geocoder = new window.BMap.Geocoder()
  const point = new window.BMap.Point(lng, lat)
  
  geocoder.getLocation(point, (result) => {
    let address = ''
    if (result && result.addressComponents) {
      const addr = result.addressComponents
      address = (addr.province || '') + (addr.city || '') + (addr.district || '') + (addr.street || '') + (addr.streetNumber || '')
    }
    if (!address || address.length < 2) {
      address = `位置: ${lng.toFixed(6)}, ${lat.toFixed(6)}`
    }
    selectedLocation.value = { lat, lng, address }
  })
}

const initAmap = (container) => {
  if (!mapConfig.value.amapKey) {
    ElMessage.warning('请先配置高德地图Key')
    return
  }
  
  if (!window.AMap) {
    let url = 'https://webapi.amap.com/maps?v=2.0&key=' + mapConfig.value.amapKey + '&plugins=AMap.Geocoder'
    if (mapConfig.value.amapSecret) {
      url += '&securityJsCode=' + mapConfig.value.amapSecret
    }
    loadScript(url, () => createAmap(container))
  } else {
    createAmap(container)
  }
}

const createAmap = (container) => {
  map = new window.AMap.Map(container, {
    zoom: mapConfig.value.defaultZoom,
    center: [116.397428, 39.90923],
    viewMode: '2D'
  })
  
  marker = new window.AMap.Marker({ position: [116.397428, 39.90923] })
  
  map.on('click', (evt) => {
    const { lng, lat } = evt.lnglat
    updateMarker(lat, lng)
    reverseGeocodeAmap(lat, lng)
  })
  
  mapLoaded.value = true
}

const reverseGeocodeAmap = (lat, lng) => {
  window.AMap.plugin('AMap.Geocoder', () => {
    const geocoder = new window.AMap.Geocoder()
    geocoder.getAddress([lng, lat], (status, result) => {
      let address = ''
      if (status === 'complete' && result.regeocode) {
        address = result.regeocode.formattedAddress
      }
      if (!address || address.length < 2) {
        address = `位置: ${lng.toFixed(6)}, ${lat.toFixed(6)}`
      }
      selectedLocation.value = { lat, lng, address }
    })
  })
}

const loadScript = (src, callback) => {
  const script = document.createElement('script')
  script.src = src
  script.onload = callback
  script.onerror = () => ElMessage.error('地图加载失败')
  document.head.appendChild(script)
}

const updateMarker = (lat, lng) => {
  if (!marker) return
  
  if (currentProvider.value === 'tencent') {
    marker.setGeometries([{ id: 'marker1', position: new window.TMap.LatLng(lat, lng) }])
    map.setCenter(new window.TMap.LatLng(lat, lng))
  } else if (currentProvider.value === 'baidu') {
    marker.setPosition(new window.BMap.Point(lng, lat))
    map.addOverlay(marker)
    map.setCenter(new window.BMap.Point(lng, lat))
  } else if (currentProvider.value === 'amap') {
    marker.setPosition([lng, lat])
    map.add(marker)
    map.setCenter([lng, lat])
  }
}

const searchLocation = () => {
  if (!searchKeyword.value) return
  
  searchResults.value = []
  searching.value = true
  
  if (currentProvider.value === 'tencent' && searchService) {
    searchService.searchRegion({
      keyword: searchKeyword.value,
      region: '中国',
      page_size: 10
    }).then((result) => {
      searching.value = false
      if (result.data && result.data.length > 0) {
        searchResults.value = result.data.map(item => {
          let addr = item.address || ''
          if (addr.length < 3) {
            addr = (item.province || '') + (item.city || '') + (item.district || '')
          }
          if (addr.length < 3) {
            addr = item.title || ''
          }
          return {
            title: item.title,
            address: addr,
            lat: item.location.lat,
            lng: item.location.lng
          }
        })
      } else {
        ElMessage.warning('未找到相关地址')
      }
    }).catch((error) => {
      searching.value = false
      ElMessage.error('搜索失败')
      console.error(error)
    })
  } else {
    searching.value = false
    ElMessage.warning('请使用腾讯地图搜索')
  }
}

const selectSearchResult = (item) => {
  // 组合完整地址
  let fullAddress = item.address
  if (!fullAddress || fullAddress.length < 3) {
    fullAddress = item.title
  }
  
  // 更新选中位置
  selectedLocation.value = {
    lat: item.lat,
    lng: item.lng,
    address: fullAddress
  }
  
  // 更新地图标记
  updateMarker(item.lat, item.lng)
  
  // 清空搜索结果
  searchResults.value = []
  searchKeyword.value = item.title
  
  // 更新地址文本
  addressText.value = fullAddress
  
  // emit事件 - 只emit经纬度，地址是可选的
  if (props.modelValue !== undefined || emit('update:modelValue')) {
    emit('update:modelValue', fullAddress)
  }
  emit('update:latitude', item.lat)
  emit('update:longitude', item.lng)
  
  // 关闭对话框
  showMapDialog.value = false
  ElMessage.success('位置选择成功')
}

const confirmLocation = () => {
  if (!selectedLocation.value) return
  
  addressText.value = selectedLocation.value.address
  // 只emit经纬度，地址是可选的
  if (props.modelValue !== undefined || emit('update:modelValue')) {
    emit('update:modelValue', selectedLocation.value.address)
  }
  emit('update:latitude', selectedLocation.value.lat)
  emit('update:longitude', selectedLocation.value.lng)
  
  showMapDialog.value = false
  ElMessage.success('位置选择成功')
}

onMounted(() => {
  fetchMapConfig()
})
</script>

<style scoped>
.map-picker {
  width: 100%;
}

.map-container {
  position: relative;
}

.search-box {
  margin-bottom: 12px;
}

.search-results {
  margin-bottom: 12px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  background: #fff;
}

.search-result-item {
  padding: 10px 15px;
  cursor: pointer;
  border-bottom: 1px solid #ebeef5;
}

.search-result-item:last-child {
  border-bottom: none;
}

.search-result-item:hover {
  background: #f5f7fa;
}

.result-title {
  font-size: 14px;
  color: #303133;
  font-weight: 500;
}

.result-address {
  font-size: 12px;
  color: #909399;
  margin-top: 4px;
}

.map-view {
  width: 100%;
  height: 400px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
}

.map-status {
  padding: 20px;
}

.location-info {
  margin-top: 12px;
  padding: 12px;
  background: #f5f7fa;
  border-radius: 4px;
}

.location-info p {
  margin: 4px 0;
  font-size: 14px;
  color: #606266;
}
</style>
